import { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  createMetricAccount,
  postInstagramOAuthStart,
  getInstagramPendingOptions,
  postInstagramConfirmPage,
  postMetricInstagramSync,
  postMetricInstagramBackfill,
  postInstagramDisconnect,
  getMetricAccountsByClient,
  getMetricDashboard,
  getMetricSeries,
} from "@/lib/api";
import { Info, Instagram, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/utils";

/** Ventana [inicio, fin] en fechas UTC (YYYY-MM-DD), alineada con el backend. */
function getUtcDateRange(periodDays) {
  const now = new Date();
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (periodDays - 1));
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

const fmtInt = (n) => new Intl.NumberFormat("es-ES").format(Math.round(n ?? 0));

/** Icono TikTok (no incluido en lucide) */
function TikTokBrandIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  );
}

function MetricTitleWithHint({ title, children, className }) {
  return (
    <div className={cn("flex w-full items-start justify-between gap-2", className)}>
      <span className="text-sm font-medium text-muted-foreground leading-snug pr-1">{title}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="shrink-0 mt-0.5 rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Información: ${title}`}
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="left"
          className="max-w-[min(22rem,92vw)] border bg-popover p-3 text-popover-foreground shadow-md"
        >
          <div className="text-xs leading-relaxed space-y-2">{children}</div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

const PIE_COLORS = ["#111827", "#2563eb", "#7c3aed", "#db2777", "#ea580c", "#ca8a04", "#16a34a"];

export default function MetricasDetailPage() {
  const { clientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clients } = useOutletContext();
  const [accounts, setAccounts] = useState([]);
  const [activeAccountId, setActiveAccountId] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingTiktok, setAddingTiktok] = useState(false);
  const [tiktokName, setTiktokName] = useState("");
  const [tiktokHandle, setTiktokHandle] = useState("");
  const [backfilling, setBackfilling] = useState(false);

  const [pickOpen, setPickOpen] = useState(false);
  const [pickSessionId, setPickSessionId] = useState("");
  const [pickPages, setPickPages] = useState([]);
  const [pickSelected, setPickSelected] = useState("");
  const [pickLoading, setPickLoading] = useState(false);
  const [pickConfirming, setPickConfirming] = useState(false);

  /** 1 | 7 | 30 | 90 — dashboard + gráfica usan la misma ventana */
  const [rangeDays, setRangeDays] = useState(7);
  const [dashLoading, setDashLoading] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [igModalOpen, setIgModalOpen] = useState(false);
  const [tiktokModalOpen, setTiktokModalOpen] = useState(false);

  const activeClient = useMemo(() => (clients || []).find((c) => c.id === clientId), [clients, clientId]);
  const activeAccount = useMemo(() => (accounts || []).find((a) => a.id === activeAccountId), [accounts, activeAccountId]);
  const instagramPickSid = searchParams.get("instagram_pick");

  const loadAccounts = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const data = await getMetricAccountsByClient(clientId);
      const list = data.accounts || [];
      setAccounts(list);
      if (list.length > 0) setActiveAccountId((prev) => prev || list[0].id);
      else setActiveAccountId("");
    } catch (_) {
      toast.error("Error al cargar cuentas");
      setAccounts([]);
      setActiveAccountId("");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    const connected = searchParams.get("instagram_connected");
    const err = searchParams.get("instagram_error");
    if (connected === "1") {
      const an = searchParams.get("account_name");
      const ih = searchParams.get("ig_handle");
      toast.success(
        an || ih
          ? `Instagram conectado: ${[an, ih].filter(Boolean).join(" · ")}`
          : "Instagram conectado correctamente"
      );
      const next = new URLSearchParams(searchParams);
      next.delete("instagram_connected");
      next.delete("account_name");
      next.delete("ig_handle");
      setSearchParams(next, { replace: true });
      loadAccounts();
    }
    if (err) {
      const map = {
        meta_denied: "Permisos cancelados en Meta",
        disabled: "OAuth desactivado en el servidor",
        missing_state: "Sesión de OAuth inválida; vuelve a conectar",
        invalid_state: "El enlace de OAuth expiró; vuelve a conectar",
        missing_code: "Meta no devolvió código",
        no_pages: "No hay páginas de Facebook o faltan permisos",
        no_instagram_business: "Ninguna página tiene Instagram profesional vinculado",
        no_page_for_fb_id: "No se encontró esa página de Facebook o sin Instagram vinculado",
        incomplete_meta: "Respuesta incompleta de Meta",
        account_missing: "La cuenta ya no existe",
        client_missing: "Cliente no encontrado",
        unknown: "Error al conectar Instagram",
      };
      toast.error(map[err] || map.unknown);
      const next = new URLSearchParams(searchParams);
      next.delete("instagram_error");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, loadAccounts]);

  const clearPickQuery = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("instagram_pick");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!instagramPickSid || !clientId) return;

    setPickSessionId(instagramPickSid);
    setPickOpen(true);
    setPickLoading(true);
    setPickPages([]);
    setPickSelected("");

    getInstagramPendingOptions(instagramPickSid, clientId)
      .then((data) => {
        const pages = data.pages || [];
        setPickPages(pages);
        if (pages.length > 0) {
          setPickSelected(String(pages[0].facebook_page_id || ""));
        }
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || "No se pudo cargar las cuentas de Instagram. Vuelve a conectar.");
        setPickOpen(false);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete("instagram_pick");
          return next;
        }, { replace: true });
      })
      .finally(() => setPickLoading(false));
  }, [instagramPickSid, clientId, setSearchParams]);

  const loadDashboardData = useCallback(async () => {
    if (!activeAccountId) {
      setDashboard(null);
      setSeries([]);
      return;
    }
    setDashLoading(true);
    const { startDate, endDate } = getUtcDateRange(rangeDays);
    try {
      const [d, s] = await Promise.all([
        getMetricDashboard(activeAccountId, { periodDays: rangeDays }),
        getMetricSeries(activeAccountId, { startDate, endDate }),
      ]);
      setDashboard(d);
      setSeries(s.series || []);
    } catch (_) {
      toast.error("Error al cargar métricas");
      setDashboard(null);
      setSeries([]);
    } finally {
      setDashLoading(false);
    }
  }, [activeAccountId, rangeDays]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleCreateTiktokAccount = async () => {
    if (!clientId) return;
    const name = tiktokName.trim();
    if (!name) return toast.error("Indica el nombre de la cuenta");
    setAddingTiktok(true);
    try {
      const created = await createMetricAccount({
        client_id: clientId,
        name,
        platform: "tiktok",
        handle: tiktokHandle.trim(),
      });
      setTiktokName("");
      setTiktokHandle("");
      setAccounts((prev) => [created, ...prev]);
      setActiveAccountId(created.id);
      toast.success("Cuenta TikTok creada (las métricas reales dependerán de la integración futura).");
      setTiktokModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo crear la cuenta");
    } finally {
      setAddingTiktok(false);
    }
  };

  /**
   * @param reconnect - si true, reutiliza la cuenta métricas Instagram seleccionada
   * @param logoutFirst - si true, abre primero logout de Facebook y luego el mismo OAuth (para elegir otro usuario de Facebook)
   */
  const startInstagramOAuth = async (reconnect, logoutFirst = false) => {
    if (!clientId) return;
    const payload = { client_id: clientId };
    if (reconnect && activeAccountId) {
      const acc = accounts.find((a) => a.id === activeAccountId);
      if (!acc || acc.platform !== "instagram") {
        toast.error("Selecciona una cuenta Instagram en el desplegable para reconectar.");
        return;
      }
      payload.metric_account_id = activeAccountId;
    }
    try {
      const data = await postInstagramOAuthStart(payload);
      if (!data.enabled) return toast.message(data.message || "OAuth de Instagram no está activo");
      const target =
        logoutFirst && data.logout_then_auth_url ? data.logout_then_auth_url : data.auth_url;
      window.location.assign(target);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo iniciar OAuth");
    }
  };

  const handleConfirmPick = async () => {
    if (!pickSessionId || !pickSelected) {
      toast.error("Selecciona una cuenta");
      return;
    }
    setPickConfirming(true);
    try {
      const data = await postInstagramConfirmPage(pickSessionId, pickSelected);
      setPickOpen(false);
      clearPickQuery();
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
        return;
      }
      toast.success(
        data.account_name || data.ig_handle
          ? `Instagram conectado: ${[data.account_name, data.ig_handle].filter(Boolean).join(" · ")}`
          : "Instagram conectado"
      );
      await loadAccounts();
      if (data.metric_account_id) setActiveAccountId(data.metric_account_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo confirmar la cuenta");
    } finally {
      setPickConfirming(false);
    }
  };

  const handleCancelPick = () => {
    setPickOpen(false);
    clearPickQuery();
  };

  const syncInstagram = async () => {
    if (!activeAccountId) return;
    try {
      await postMetricInstagramSync(activeAccountId);
      toast.success("Métricas sincronizadas");
      await loadDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al sincronizar");
    }
  };

  const backfillInstagram = async () => {
    if (!activeAccountId) return;
    const acc = accounts.find((a) => a.id === activeAccountId);
    if (acc && acc.platform !== "instagram") {
      toast.error("Solo aplica a cuentas Instagram");
      return;
    }
    setBackfilling(true);
    try {
      const data = await postMetricInstagramBackfill(activeAccountId, 90);
      const n = data.days_written ?? 0;
      toast.success(`Histórico importado: ${n} días con datos (solicitados ${data.days_requested ?? 90}).`);
      if (Array.isArray(data.warnings) && data.warnings.length > 0) {
        toast.message(data.warnings.slice(0, 2).join(" · "), { duration: 8000 });
      }
      try {
        await postMetricInstagramSync(activeAccountId);
      } catch (_) {
        /* sync opcional: refresca seguidores/publicaciones del día desde el perfil */
      }
      await loadDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar histórico");
    } finally {
      setBackfilling(false);
    }
  };

  const handleConfirmDisconnect = async () => {
    if (!activeAccountId) return;
    setDisconnecting(true);
    try {
      await postInstagramDisconnect(activeAccountId);
      toast.success("Instagram desconectado. Los datos históricos en el panel se conservan.");
      setDisconnectOpen(false);
      await loadAccounts();
      await loadDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo desconectar");
    } finally {
      setDisconnecting(false);
    }
  };

  const isIgActive = activeAccount?.platform === "instagram";
  /** Cuenta vinculada a Meta (tokens / perfil IG), no solo ficha manual */
  const igLinked =
    isIgActive &&
    (activeAccount?.provider_status === "connected" || Boolean(activeAccount?.instagram_username));

  /** Impresiones agregadas por día de la semana (UTC) para donut */
  const pieImpressionsByWeekday = useMemo(() => {
    const labels = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
    const sums = [0, 0, 0, 0, 0, 0, 0];
    for (const row of series) {
      const d = new Date(`${row.date}T12:00:00.000Z`);
      const w = d.getUTCDay();
      sums[w] += Number(row.views || 0);
    }
    return labels.map((name, i) => ({ name, value: sums[i] })).filter((x) => x.value > 0);
  }, [series]);

  /** Días con subida / bajada / igual de seguidores entre días consecutivos */
  const pieFollowerDays = useMemo(() => {
    const sorted = [...series].sort((a, b) => String(a.date).localeCompare(String(b.date)));
    let up = 0;
    let down = 0;
    let flat = 0;
    for (let i = 1; i < sorted.length; i++) {
      const df = Number(sorted[i].followers || 0) - Number(sorted[i - 1].followers || 0);
      if (df > 0) up++;
      else if (df < 0) down++;
      else flat++;
    }
    const out = [];
    if (up > 0) out.push({ name: "Días ganando seguidores", value: up });
    if (down > 0) out.push({ name: "Días perdiendo seguidores", value: down });
    if (flat > 0) out.push({ name: "Días sin cambio", value: flat });
    return out;
  }, [series]);

  /** Top fechas por impresiones (barras) */
  const topImpressionDays = useMemo(() => {
    return [...series]
      .map((r) => ({ date: r.date, views: Number(r.views || 0) }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 5)
      .filter((x) => x.views > 0);
  }, [series]);

  const periodLabel =
    rangeDays === 1 ? "1 día" : rangeDays === 7 ? "7 días" : rangeDays === 30 ? "30 días" : "90 días";

  if (!clientId) {
    return (
      <div className="p-8">
        <Card><CardContent className="p-6">Selecciona un cliente en el sidebar.</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1600px] mx-auto p-4 sm:p-6 space-y-6">
      <Dialog open={pickOpen} onOpenChange={(o) => { if (!o) handleCancelPick(); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>¿Qué Instagram quieres usar?</DialogTitle>
            <DialogDescription>
              Tu usuario de Facebook tiene varias páginas con Instagram profesional vinculado. Elige la que corresponda a este cliente.
            </DialogDescription>
          </DialogHeader>
          {pickLoading ? (
            <div className="flex items-center gap-2 py-6 text-muted-foreground text-sm">
              <Loader2 className="w-5 h-5 animate-spin" />
              Cargando opciones…
            </div>
          ) : pickPages.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No hay opciones disponibles.</p>
          ) : (
            <RadioGroup value={pickSelected} onValueChange={setPickSelected} className="gap-3">
              {pickPages.map((p) => {
                const id = String(p.facebook_page_id || "");
                return (
                  <div key={id} className="flex items-start gap-3 rounded-lg border p-3 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring">
                    <RadioGroupItem value={id} id={`ig-pick-${id}`} className="mt-1" />
                    <Label htmlFor={`ig-pick-${id}`} className="flex-1 cursor-pointer text-sm font-normal leading-snug">
                      <span className="font-medium block">{p.page_name || "Página"}</span>
                      {(p.instagram_display_name || p.instagram_username) && (
                        <span className="text-muted-foreground block text-xs mt-0.5">
                          Instagram: {p.instagram_display_name || `@${p.instagram_username}`}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground block mt-1">ID página: {id}</span>
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="ghost" onClick={handleCancelPick} disabled={pickConfirming}>
              Cancelar
            </Button>
            <Button type="button" onClick={handleConfirmPick} disabled={pickLoading || !pickSelected || pickConfirming}>
              {pickConfirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Usar esta cuenta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-semibold">{activeClient?.name || "Cliente"}</h2>
          <p className="text-sm text-muted-foreground">Dashboard de analítica social</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-11 w-11 shrink-0 rounded-full"
                onClick={() => setIgModalOpen(true)}
                aria-label="Conexión Instagram"
              >
                <Instagram className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Conectar o reconectar Instagram (Meta)</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-11 w-11 shrink-0 rounded-full"
                onClick={() => setTiktokModalOpen(true)}
                aria-label="Ficha TikTok manual"
              >
                <TikTokBrandIcon className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Añadir cuenta TikTok (manual)</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <Dialog open={igModalOpen} onOpenChange={setIgModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Instagram className="h-5 w-5" />
              Instagram (Meta)
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-sm text-muted-foreground space-y-2 pt-1">
                <p>
                  <strong className="text-foreground">Paso a paso:</strong> Instagram profesional va unido a una <strong>página de Facebook</strong>. El login es de <strong>Facebook</strong> (no instagram.com): es el flujo oficial de Meta.
                </p>
                <p>
                  Si solo ves «¿Continuar?» y siempre la misma cuenta, usa <strong>Otra cuenta de Facebook</strong> para cerrar sesión en Facebook y volver a autorizar.
                </p>
                <p>
                  Si hay <strong>varias páginas</strong> con Instagram, al volver te pediremos cuál vincular. Si solo hay una, se conecta sola.
                </p>
                <p>
                  <strong>Desconectar</strong> borra los datos en Brutalco (no revoca la app en Facebook).
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 sm:grid-cols-2">
            <Button className="w-full" onClick={() => { setIgModalOpen(false); startInstagramOAuth(false, false); }} disabled={!clientId}>
              Conectar Instagram
            </Button>
            <Button type="button" variant="outline" className="w-full" onClick={() => { setIgModalOpen(false); startInstagramOAuth(false, true); }} disabled={!clientId}>
              Otra cuenta de Facebook
            </Button>
          </div>
          {accounts.some((a) => a.platform === "instagram") && (
            <div className="grid gap-2 sm:grid-cols-2">
              <Button variant="secondary" className="w-full" onClick={() => { setIgModalOpen(false); startInstagramOAuth(true, false); }} disabled={!activeAccountId || !isIgActive}>
                Reconectar permisos
              </Button>
              <Button type="button" variant="outline" className="w-full" onClick={() => { setIgModalOpen(false); startInstagramOAuth(true, true); }} disabled={!activeAccountId || !isIgActive}>
                Reconectar con otra cuenta FB
              </Button>
            </div>
          )}
          {igLinked && (
            <Button
              type="button"
              variant="outline"
              className="w-full text-destructive border-destructive/40 hover:bg-destructive/10"
              onClick={() => { setIgModalOpen(false); setDisconnectOpen(true); }}
              disabled={!activeAccountId}
            >
              Desconectar Instagram
            </Button>
          )}
          <p className="text-xs text-muted-foreground border-t pt-3">
            Por defecto usamos <code className="text-xs bg-muted px-1 rounded">auth_type=reauthenticate</code>. La cuenta IG debe ser <strong>profesional</strong> y enlazada a una página de Facebook que gestiones con ese usuario.
          </p>
        </DialogContent>
      </Dialog>

      <Dialog open={tiktokModalOpen} onOpenChange={setTiktokModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TikTokBrandIcon className="h-5 w-5" />
              TikTok (manual)
            </DialogTitle>
            <DialogDescription>
              TikTok aún no tiene OAuth en este panel: crea la ficha con nombre y @ para organizar clientes; las métricas reales se integrarán después.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="space-y-2">
              <Label>Nombre visible</Label>
              <Input value={tiktokName} onChange={(e) => setTiktokName(e.target.value)} placeholder="Ej. Marca TikTok" />
            </div>
            <div className="space-y-2">
              <Label>Handle</Label>
              <Input value={tiktokHandle} onChange={(e) => setTiktokHandle(e.target.value)} placeholder="@usuario" />
            </div>
            <Button variant="default" className="w-full" onClick={handleCreateTiktokAccount} disabled={addingTiktok}>
              {addingTiktok ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Añadir cuenta TikTok
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex items-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-2" />Cargando cuentas…</div>
      ) : accounts.length === 0 ? (
        <Card className="bg-card border border-dashed shadow-sm">
          <CardContent className="p-6 text-sm text-muted-foreground">
            Aún no hay cuentas para este cliente. Pulsa el icono de <strong>Instagram</strong> o <strong>TikTok</strong> arriba a la derecha para conectar o crear una ficha.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Cuenta activa</Label>
              <Select value={activeAccountId} onValueChange={setActiveAccountId}>
                <SelectTrigger><SelectValue placeholder="Selecciona cuenta" /></SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.name}
                      {acc.handle ? ` ${acc.handle}` : ""}
                      {" "}
                      <span className="text-muted-foreground">({acc.platform})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {activeAccount && (
              <Card className="bg-card border shadow-sm">
                <CardContent className="p-4 text-sm space-y-1">
                  <p><span className="text-muted-foreground">Nombre:</span> {activeAccount.name || "—"}</p>
                  {activeAccount.handle ? <p><span className="text-muted-foreground">Handle:</span> {activeAccount.handle}</p> : null}
                  {activeAccount.instagram_username ? (
                    <p><span className="text-muted-foreground">Usuario IG:</span> @{activeAccount.instagram_username}</p>
                  ) : null}
                  <p><span className="text-muted-foreground">Estado:</span> {activeAccount.provider_status || "—"}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {activeAccountId && isIgActive && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={syncInstagram}>
                Sincronizar hoy (Graph API)
              </Button>
              <Button variant="outline" onClick={backfillInstagram} disabled={backfilling}>
                {backfilling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Importar últimos 90 días
              </Button>
            </div>
          )}

          <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">Rango temporal</p>
                <p className="text-xs text-muted-foreground">
                  KPIs y gráfica: {dashboard?.period_start && dashboard?.period_end
                    ? `${dashboard.period_start} → ${dashboard.period_end} (UTC)`
                    : "—"}
                </p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <Info className="h-3.5 w-3.5 shrink-0" />
                    ¿Por qué “me gusta” en 0?
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="start"
                  className="max-w-[min(22rem,92vw)] border bg-popover p-3 text-popover-foreground shadow-md"
                >
                  <p className="text-xs font-medium mb-1.5">Ceros en me gusta / comentarios</p>
                  <div className="text-xs leading-relaxed space-y-2">
                    <p>
                      El histórico que importamos rellena sobre todo <strong>seguidores por día</strong> e <strong>impresiones</strong>. Los me gusta y comentarios a nivel “toda la cuenta” <strong>no</strong> vienen en ese mismo informe; en el snapshot los dejamos en <strong>0</strong>.
                    </p>
                    <p>
                      Por eso el % engagement suele ser 0: no significa que Meta falle, sino que <strong>aquí aún no mostramos</strong> esos totales.
                    </p>
                    <p>
                      Impresiones del último día (snapshot): <strong>{fmtInt(dashboard?.current_metrics?.views ?? 0)}</strong> — la suma del periodo está en la tarjeta “Impresiones (periodo)”.
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
            <Tabs value={String(rangeDays)} onValueChange={(v) => setRangeDays(Number(v))}>
              <TabsList className="flex w-full flex-wrap h-auto gap-1 sm:w-auto">
                <TabsTrigger value="1" className="text-xs sm:text-sm">1 día</TabsTrigger>
                <TabsTrigger value="7" className="text-xs sm:text-sm">7 días</TabsTrigger>
                <TabsTrigger value="30" className="text-xs sm:text-sm">30 días</TabsTrigger>
                <TabsTrigger value="90" className="text-xs sm:text-sm">90 días</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {dashLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Actualizando métricas…
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="bg-card border shadow-sm">
              <CardHeader className="pb-2 space-y-0">
                <MetricTitleWithHint title="Seguidores (total)">
                  <p>
                    Personas que te siguen en el <strong>día más reciente</strong> guardado en este rango ({periodLabel}). Es el total de ese día, no un promedio.
                  </p>
                  <p className="text-amber-800 dark:text-amber-200">
                    ¿0 tras importar 90 días? Pulsa <strong>Sincronizar hoy</strong>: el import es histórico; el sync lee el total actual del perfil.
                  </p>
                </MetricTitleWithHint>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold tabular-nums">{fmtInt(dashboard?.current_metrics?.followers)}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border shadow-sm">
              <CardHeader className="pb-2 space-y-0">
                <MetricTitleWithHint title="Siguiendo">
                  <p>Cuántas cuentas sigue este perfil (viene del perfil de Instagram al sincronizar, no de la gráfica histórica).</p>
                </MetricTitleWithHint>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold tabular-nums">{fmtInt(dashboard?.current_metrics?.following)}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border shadow-sm">
              <CardHeader className="pb-2 space-y-0">
                <MetricTitleWithHint title="Publicaciones">
                  <p>
                    {rangeDays > 1 && dashboard?.posts_delta != null && dashboard.posts_delta !== 0
                      ? <>Cambio en el periodo: <strong>{dashboard.posts_delta > 0 ? "+" : ""}{fmtInt(dashboard.posts_delta)}</strong>. </>
                      : null}
                    Total de publicaciones que Instagram asocia al perfil (posts, reels…).
                  </p>
                </MetricTitleWithHint>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold tabular-nums">{fmtInt(dashboard?.current_metrics?.posts)}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border shadow-sm">
              <CardHeader className="pb-2 space-y-0">
                <MetricTitleWithHint title="Δ Seguidores">
                  <p>
                    Diferencia de seguidores entre el <strong>primer</strong> y el <strong>último</strong> día con datos en este rango (no solo “hoy”).
                  </p>
                </MetricTitleWithHint>
              </CardHeader>
              <CardContent>
                <p className={`text-3xl font-semibold tabular-nums ${(dashboard?.followers_delta ?? dashboard?.followers_growth_7d ?? 0) >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}`}>
                  {(dashboard?.followers_delta ?? dashboard?.followers_growth_7d ?? 0) >= 0 ? "+" : ""}
                  {fmtInt(dashboard?.followers_delta ?? dashboard?.followers_growth_7d ?? 0)}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="bg-card border shadow-sm">
              <CardHeader className="pb-2 space-y-0">
                <MetricTitleWithHint title="Impresiones (periodo)">
                  <p>
                    <strong>Suma</strong> de impresiones (vistas aproximadas de tu contenido) en todos los días del rango, según insights de Instagram.
                  </p>
                </MetricTitleWithHint>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold tabular-nums">{fmtInt(dashboard?.views_period_total ?? 0)}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border shadow-sm">
              <CardHeader className="pb-2 space-y-0">
                <MetricTitleWithHint title="Media impresiones / día">
                  <p>Impresiones totales del periodo divididas entre los días que tienen datos (no implica que cada día tuviera ese mínimo).</p>
                </MetricTitleWithHint>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold tabular-nums">{fmtInt(dashboard?.avg_daily_views ?? 0)}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border shadow-sm">
              <CardHeader className="pb-2 space-y-0">
                <MetricTitleWithHint title="Engagement %">
                  <p>
                    (Me gusta + comentarios del último snapshot) ÷ seguidores. Como no rellenamos likes/comentarios en el import, casi siempre sale <strong>0 %</strong>; no juzga la calidad real de tu cuenta aquí.
                  </p>
                </MetricTitleWithHint>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold tabular-nums">{((dashboard?.engagement_rate || 0) * 100).toFixed(2)}%</p>
              </CardContent>
            </Card>
            <Card className="bg-card border shadow-sm">
              <CardHeader className="pb-2 space-y-0">
                <MetricTitleWithHint title="Días con datos">
                  <p>
                    Cuántos días hay guardados en base de datos en esta ventana (máximo {rangeDays} en el rango {periodLabel}).
                  </p>
                </MetricTitleWithHint>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold tabular-nums">{fmtInt(dashboard?.days_with_data ?? 0)}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-card border shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Evolución: seguidores e impresiones</CardTitle>
              <CardDescription>
                Misma ventana que las pestañas ({periodLabel}). Eje derecho: impresiones diarias si el histórico las incluye.
              </CardDescription>
            </CardHeader>
            <CardContent className="h-80 w-full min-h-[280px]">
              {series.length === 0 ? (
                <p className="text-sm text-muted-foreground py-12 text-center">No hay puntos en este rango. Sincroniza o importa histórico.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => fmtInt(v)} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => fmtInt(v)} />
                    <RechartsTooltip
                      formatter={(value, name) => [fmtInt(value), name === "followers" ? "Seguidores" : "Impresiones"]}
                      labelFormatter={(l) => `Fecha: ${l}`}
                    />
                    <Legend formatter={(value) => (value === "followers" ? "Seguidores" : "Impresiones (vistas)")} />
                    <Line yAxisId="left" type="monotone" dataKey="followers" name="followers" stroke="#111827" strokeWidth={2} dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="views" name="views" stroke="#2563eb" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="bg-card border shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Impresiones por día de la semana</CardTitle>
                <CardDescription className="text-xs">Suma de impresiones del rango agrupadas por Lun–Dom (UTC). Útil para ver en qué días hubo más alcance.</CardDescription>
              </CardHeader>
              <CardContent className="h-[260px]">
                {pieImpressionsByWeekday.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-10">Sin impresiones en el periodo para este gráfico.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieImpressionsByWeekday}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={48}
                        outerRadius={80}
                        paddingAngle={2}
                      >
                        {pieImpressionsByWeekday.map((entry, i) => (
                          <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(v) => fmtInt(v)} />
                      <Legend layout="horizontal" verticalAlign="bottom" wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
            <Card className="bg-card border shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Días ganando vs perdiendo seguidores</CardTitle>
                <CardDescription className="text-xs">
                  Compara cada día con el anterior: cuántos días subió, bajó o se mantuvo el número de seguidores (según datos guardados).
                </CardDescription>
              </CardHeader>
              <CardContent className="h-[260px]">
                {pieFollowerDays.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-10">Hace falta al menos 2 días con datos para comparar.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieFollowerDays}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={48}
                        outerRadius={80}
                        paddingAngle={2}
                      >
                        {pieFollowerDays.map((entry, i) => (
                          <Cell key={entry.name} fill={PIE_COLORS[(i + 2) % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(v) => fmtInt(v)} />
                      <Legend layout="horizontal" verticalAlign="bottom" wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
            <Card className="bg-card border shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Top días por impresiones</CardTitle>
                <CardDescription className="text-xs">Las 5 fechas con más impresiones en el rango seleccionado.</CardDescription>
              </CardHeader>
              <CardContent className="h-[260px]">
                {topImpressionDays.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-10">Sin datos de impresiones.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topImpressionDays} layout="vertical" margin={{ left: 8, right: 8, top: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal />
                      <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => fmtInt(v)} />
                      <YAxis type="category" dataKey="date" width={88} tick={{ fontSize: 10 }} />
                      <RechartsTooltip formatter={(v) => fmtInt(v)} labelFormatter={() => "Impresiones"} />
                      <Bar dataKey="views" fill="#2563eb" radius={[0, 4, 4, 0]} name="Impresiones" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="bg-card border shadow-sm">
            <CardHeader><CardTitle className="text-base">Insights automáticos</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(dashboard?.insights || []).map((item, idx) => (
                <p key={`${item}-${idx}`} className="text-sm text-foreground/90 border-l-2 border-primary/40 pl-3 py-0.5">{item}</p>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-card border shadow-sm border-primary/30">
            <CardHeader><CardTitle className="text-base">Recomendación</CardTitle></CardHeader>
            <CardContent className="text-sm font-medium leading-relaxed">{dashboard?.recommendation || "Sin recomendación disponible."}</CardContent>
          </Card>
        </>
      )}

      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desconectar Instagram?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán los tokens guardados en Brutalco para esta cuenta. El histórico de métricas en el panel no se borra.
              La próxima vez que conectes, Facebook pedirá inicio de sesión de nuevo para elegir usuario y páginas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnecting}>Cancelar</AlertDialogCancel>
            <Button variant="destructive" onClick={handleConfirmDisconnect} disabled={disconnecting}>
              {disconnecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Desconectar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
